'use client'
import React, { createContext, useEffect, useReducer, useState } from 'react'
import { CartReducer, initialState, actionType, initialStateType } from './CartReducer'
import cartService from "../../cart"
import { v4 as uuidv4 } from 'uuid'

// Define the type for the Thunk function - a thunk that takes a basic dispatcher and state
type AppThunk = (dispatch: (action: actionType) => void, state: initialStateType) => Promise<void>

// Define the DispatchType, which can be a simple action or a thunk
export type DispatchType = (action: actionType | AppThunk) => void

// Define the context types
export const CartContext = createContext<initialStateType>(initialState)

// Default value for the dispatch context should be null for proper error handling
export const CartDispatchContext = createContext<DispatchType | null>(null)

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [cart, _dispatch] = useReducer(CartReducer, initialState)
  const [uuid, setUuid] = useState<string>('')

  // Create the dispatch function that handles both actions and thunks
  const dispatch: DispatchType = (actionOrThunk) => {
    if (typeof actionOrThunk === 'function') {
      // Action is a thunk, so we invoke it with _dispatch and state
      actionOrThunk(_dispatch, { ...cart, uuid })
    } else {
      // Action is a plain action, so we invoke the reducer directly
      _dispatch(actionOrThunk)
    }
  }

  // Function to sync the cart from the server
  const syncCart = async (uuid: string) => {
    try {
      const serverCart = await cartService.fetchCart(uuid)
      dispatch({ type: 'SET_CART', payload: serverCart })
    } catch (error) {
      console.error('Failed to sync cart:', error)
      dispatch({ type: 'EMPTY_CART' })
    }
  }

  useEffect(() => {
    // Get uuid from localStorage or create a new one if not found
    let storedUuid = localStorage.getItem('basmti_cart_uuid')
    if (!storedUuid) {
      storedUuid = uuidv4()
      localStorage.setItem('basmti_cart_uuid', storedUuid)
    }
    setUuid(storedUuid)
    syncCart(storedUuid)
  }, [])

  return (
    <CartContext.Provider value={{ ...cart, uuid }}>
      <CartDispatchContext.Provider value={dispatch}>
        {children}
      </CartDispatchContext.Provider>
    </CartContext.Provider>
  )
}

// Custom hook to use the cart state
export function useCartState() {
  const context = React.useContext(CartContext)
  if (context === undefined) {
    throw new Error('useCartState must be used within a CartProvider')
  }
  return context
}

// Custom hook to use the dispatch function
export function useCartDispatch() {
  const context = React.useContext(CartDispatchContext)
  if (context === null) {
    throw new Error('useCartDispatch must be used within a CartProvider')
  }
  return context
}
